import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  Alert,
  MenuItem,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Checkbox,
  Tabs,
  Tab,
  Autocomplete
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import GavelIcon from '@mui/icons-material/Gavel';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import PDFViewer from '../components/PDFViewer';

function Analyzer() {
  const [searchParams] = useSearchParams();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [cases, setCases] = useState([]);
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [analysisType, setAnalysisType] = useState('analysis');
  const [customPrompt, setCustomPrompt] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Constitutional analysis state
  const [constitutionalAnalysis, setConstitutionalAnalysis] = useState(null);
  const [constitutionalAnalyzing, setConstitutionalAnalyzing] = useState(false);
  const [constitutionalAnalysisType, setConstitutionalAnalysisType] = useState('analysis');
  const [constitutionalCustomPrompt, setConstitutionalCustomPrompt] = useState('');

  // Bare Acts analysis state
  const [bareActsAnalysis, setBareActsAnalysis] = useState(null);
  const [bareActsAnalyzing, setBareActsAnalyzing] = useState(false);
  const [bareActsAnalysisType, setBareActsAnalysisType] = useState('analysis');
  const [bareActsCustomPrompt, setBareActsCustomPrompt] = useState('');
  
  // Bare Acts individual RAG state
  const [availableBareActs, setAvailableBareActs] = useState([]);
  const [selectedBareActs, setSelectedBareActs] = useState([]);
  const [bareActsLoading, setBareActsLoading] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [trainingInProgress, setTrainingInProgress] = useState(false);

  // Custom Model analysis state
  const [customCategories, setCustomCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customAnalysis, setCustomAnalysis] = useState(null);
  const [customAnalyzing, setCustomAnalyzing] = useState(false);
  const [customAnalysisType, setCustomAnalysisType] = useState('analysis');
  const [customCustomPrompt, setCustomCustomPrompt] = useState('');

  // Criminal Law analysis state
  const [criminalLawAnalysis, setCriminalLawAnalysis] = useState(null);
  const [criminalLawAnalyzing, setCriminalLawAnalyzing] = useState(false);
  const [criminalLawAnalysisType, setCriminalLawAnalysisType] = useState('analysis');
  const [criminalLawCustomPrompt, setCriminalLawCustomPrompt] = useState('');
  const [availableCriminalLaws, setAvailableCriminalLaws] = useState([]);
  const [selectedCriminalLaws, setSelectedCriminalLaws] = useState([]);
  const [criminalLawLoading, setCriminalLawLoading] = useState(false);

  // Civil Law analysis state
  const [civilLawAnalysis, setCivilLawAnalysis] = useState(null);
  const [civilLawAnalyzing, setCivilLawAnalyzing] = useState(false);
  const [civilLawAnalysisType, setCivilLawAnalysisType] = useState('analysis');
  const [civilLawCustomPrompt, setCivilLawCustomPrompt] = useState('');
  const [availableCivilLaws, setAvailableCivilLaws] = useState([]);
  const [selectedCivilLaws, setSelectedCivilLaws] = useState([]);
  const [civilLawLoading, setCivilLawLoading] = useState(false);

  // Family Law analysis state
  const [familyLawAnalysis, setFamilyLawAnalysis] = useState(null);
  const [familyLawAnalyzing, setFamilyLawAnalyzing] = useState(false);
  const [familyLawAnalysisType, setFamilyLawAnalysisType] = useState('analysis');
  const [familyLawCustomPrompt, setFamilyLawCustomPrompt] = useState('');
  const [availableFamilyLaws, setAvailableFamilyLaws] = useState([]);
  const [selectedFamilyLaws, setSelectedFamilyLaws] = useState([]);

  // PDF Viewer state
  const [analyzedDocuments, setAnalyzedDocuments] = useState([]);
  const [familyLawLoading, setFamilyLawLoading] = useState(false);
  
  // PDF Modal state
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfModalDocument, setPdfModalDocument] = useState(null);
  const [pdfModalPage, setPdfModalPage] = useState(1);

  // History state
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [constitutionalHistory, setConstitutionalHistory] = useState([]);
  const [bareActsHistory, setBareActsHistory] = useState([]);
  const [customHistory, setCustomHistory] = useState([]);
  const [criminalLawHistory, setCriminalLawHistory] = useState([]);
  const [civilLawHistory, setCivilLawHistory] = useState([]);
  const [familyLawHistory, setFamilyLawHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  // Chat state
  const [chatSessions, setChatSessions] = useState([]);
  const [currentChatSession, setCurrentChatSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);

  useEffect(() => {
    fetchCases();
    fetchCustomCategories();
  }, []);
  
  // Fetch available Bare Acts when switching to Bare Acts tab
  useEffect(() => {
    if (tabValue === 2) {
      fetchAvailableBareActs();
    } else if (tabValue === 3) {
      fetchAvailableCriminalLaws();
    } else if (tabValue === 4) {
      fetchAvailableCivilLaws();
    } else if (tabValue === 5) {
      fetchAvailableFamilyLaws();
    }
  }, [tabValue]);

  const fetchCustomCategories = async () => {
    try {
      const response = await apiService.getCustomCategories();
      const trained = response.data.categories.filter(cat => cat.rag_trained);
      setCustomCategories(trained);
    } catch (err) {
      console.error('Error fetching custom categories:', err);
    }
  };
  
  const fetchAvailableBareActs = async () => {
    try {
      setBareActsLoading(true);
      const response = await apiService.getBareActsList();
      setAvailableBareActs(response.data.acts || []);
    } catch (err) {
      console.error('Error fetching bare acts:', err);
      setError('Failed to load Bare Acts list');
    } finally {
      setBareActsLoading(false);
    }
  };

  const fetchAvailableCriminalLaws = async () => {
    try {
      setCriminalLawLoading(true);
      const response = await apiService.getCriminalLawsList();
      setAvailableCriminalLaws(response.data.laws || []);
    } catch (err) {
      console.error('Error fetching criminal laws:', err);
      setError('Failed to load Criminal Laws list');
    } finally {
      setCriminalLawLoading(false);
    }
  };

  const fetchAvailableCivilLaws = async () => {
    try {
      setCivilLawLoading(true);
      const response = await apiService.getCivilLawsList();
      setAvailableCivilLaws(response.data.laws || []);
    } catch (err) {
      console.error('Error fetching civil laws:', err);
      setError('Failed to load Civil Laws list');
    } finally {
      setCivilLawLoading(false);
    }
  };

  const fetchAvailableFamilyLaws = async () => {
    try {
      setFamilyLawLoading(true);
      const response = await apiService.getFamilyLawsList();
      setAvailableFamilyLaws(response.data.laws || []);
    } catch (err) {
      console.error('Error fetching family laws:', err);
      setError('Failed to load Family Laws list');
    } finally {
      setFamilyLawLoading(false);
    }
  };

  // Pre-select case from URL parameter
  useEffect(() => {
    const caseIdFromUrl = searchParams.get('caseId');
    if (caseIdFromUrl && cases.length > 0) {
      setSelectedCase(caseIdFromUrl);
    }
  }, [cases, searchParams]);

  useEffect(() => {
    if (selectedCase) {
      fetchCaseDocuments();
      fetchChatSessions();
    } else {
      setCaseDocuments([]);
      setSelectedFiles([]);
      setAnalysisHistory([]);
      setConstitutionalHistory([]);
      setChatSessions([]);
      setCurrentChatSession(null);
      setChatMessages([]);
    }
  }, [selectedCase]);

  // Fetch history after case documents are loaded
  useEffect(() => {
    if (selectedCase && caseDocuments.length >= 0) {
      fetchAllAnalysisHistory();
    }
  }, [selectedCase, caseDocuments.length]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCases();
      setCases(response.data.cases || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDocuments = async () => {
    try {
      const response = await apiService.getCaseDocuments(selectedCase);
      setCaseDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load case documents');
    }
  };

  const fetchAllAnalysisHistory = async () => {
    if (!selectedCase) return;
    
    try {
      setLoadingHistory(true);
      console.log('Fetching analysis history for case:', selectedCase);
      
      // Fetch all analyses for this case (from case_analyses collection)
      const caseAnalysesResponse = await apiService.getCaseAnalyses(selectedCase);
      const caseAnalyses = caseAnalysesResponse.data.analyses || [];
      console.log('Case analyses:', caseAnalyses.length);
      
      // Fetch all constitutional analyses for this case
      const constitutionalResponse = await apiService.getCaseConstitutionalAnalyses(selectedCase);
      const constitutionalAnalyses = constitutionalResponse.data.analyses || [];
      console.log('Constitutional analyses:', constitutionalAnalyses.length);
      
      // Fetch all Bare Acts analyses for this case
      const bareActsResponse = await apiService.getCaseBareActsAnalyses(selectedCase);
      const bareActsAnalyses = bareActsResponse.data.analyses || [];
      console.log('Bare Acts analyses:', bareActsAnalyses.length);
      
      // Also fetch document-specific analyses for all documents in this case
      const allDocAnalyses = [];
      const allDocConstitutional = [];
      
      console.log('Fetching analyses for', caseDocuments.length, 'documents');
      for (const doc of caseDocuments) {
        try {
          const docResponse = await apiService.getDocumentAnalyses(doc.id);
          const docStandard = docResponse.data.standard_analyses || [];
          const docConst = docResponse.data.constitutional_analyses || [];
          console.log(`Document ${doc.filename}: ${docStandard.length} standard, ${docConst.length} constitutional`);
          allDocAnalyses.push(...docStandard);
          allDocConstitutional.push(...docConst);
        } catch (err) {
          console.error(`Error fetching analyses for document ${doc.id}:`, err);
        }
      }
      
      // Combine all analyses and sort by date
      const combinedStandard = [...caseAnalyses, ...allDocAnalyses].sort((a, b) => {
        const dateA = a.created_at?.seconds || 0;
        const dateB = b.created_at?.seconds || 0;
        return dateB - dateA;
      });
      
      const combinedConstitutional = [...constitutionalAnalyses, ...allDocConstitutional].sort((a, b) => {
        const dateA = a.created_at?.seconds || 0;
        const dateB = b.created_at?.seconds || 0;
        return dateB - dateA;
      });
      
      const combinedBareActs = bareActsAnalyses.sort((a, b) => {
        const dateA = a.created_at?.seconds || 0;
        const dateB = b.created_at?.seconds || 0;
        return dateB - dateA;
      });
      
      // Fetch custom analyses
      let customAnalyses = [];
      try {
        const customResponse = await apiService.getCaseCustomAnalyses(selectedCase);
        customAnalyses = customResponse.data.analyses || [];
        console.log('Custom analyses:', customAnalyses.length);
      } catch (err) {
        console.error('Error fetching custom analyses:', err);
        // Continue without custom analyses
      }
      
      const combinedCustom = customAnalyses.sort((a, b) => {
        const dateA = a.created_at?.seconds || 0;
        const dateB = b.created_at?.seconds || 0;
        return dateB - dateA;
      });
      
      console.log('Total standard analyses:', combinedStandard.length);
      console.log('Total constitutional analyses:', combinedConstitutional.length);
      console.log('Total Bare Acts analyses:', combinedBareActs.length);
      console.log('Total custom analyses:', combinedCustom.length);
      
      setAnalysisHistory(combinedStandard);
      setConstitutionalHistory(combinedConstitutional);
      setBareActsHistory(combinedBareActs);
      setCustomHistory(combinedCustom);
    } catch (err) {
      console.error('Error fetching analysis history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      if (!selectedCase) {
        setError('Please select a case first before uploading documents');
        return;
      }

      const newFiles = acceptedFiles.map(file => ({
        file,
        id: Math.random().toString(36),
        name: file.name,
        size: file.size,
        uploaded: false,
        uploading: true
      }));
      setUploadedFiles([...uploadedFiles, ...newFiles]);
      
      // Upload files to the selected case
      setUploading(true);
      for (const fileObj of newFiles) {
        try {
          const response = await apiService.uploadDocument(selectedCase, fileObj.file);
          const uploadedDoc = response.data.document;
          
          // Update the file object with document ID
          fileObj.documentId = uploadedDoc.id;
          fileObj.uploaded = true;
          fileObj.uploading = false;
          
          // Refresh case documents
          await fetchCaseDocuments();
        } catch (err) {
          console.error('Error uploading file:', err);
          fileObj.uploading = false;
          fileObj.uploadError = true;
        }
      }
      setUploading(false);
      setUploadedFiles([...uploadedFiles, ...newFiles]);
    },
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    multiple: true
  });

  const quickAnalysisOptions = [
    { value: 'analysis', label: 'Perform AI Analysis' },
    { value: 'custom', label: 'Custom Prompt' }
  ];

  const constitutionalAnalysisOptions = [
    { value: 'analysis', label: 'Perform AI Analysis' },
    { value: 'custom', label: 'Custom Prompt' }
  ];

  // Handle clicking on page number to open PDF modal
  const handlePageClick = (pageNumber) => {
    if (analyzedDocuments.length > 0) {
      setPdfModalDocument(analyzedDocuments[0]);
      setPdfModalPage(pageNumber);
      setPdfModalOpen(true);
    }
  };

  // Render analysis text with clickable page numbers
  const renderAnalysisWithLinks = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Match patterns like (Page X) or (Page X, Line Y)
    const pagePattern = /\(Page\s+(\d+)(?:,\s*Line\s+\d+)?\)/gi;
    
    const parts = [];
    let lastIndex = 0;
    let match;
    let matchCount = 0;
    
    // Reset regex index
    pagePattern.lastIndex = 0;
    
    while ((match = pagePattern.exec(text)) !== null) {
      matchCount++;
      
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add clickable page number
      const pageNum = parseInt(match[1]);
      parts.push(
        <Box
          key={`page-${matchCount}-${match.index}`}
          component="span"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 600,
            '&:hover': {
              color: 'primary.dark',
              backgroundColor: 'primary.light'
            }
          }}
          onClick={(e) => {
            e.preventDefault();
            handlePageClick(pageNum);
          }}
        >
          {match[0]}
        </Box>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Parse annotations from analysis text
  const parseAnnotations = (analysisText, documentId) => {
    console.log(`parseAnnotations called for document ${documentId}, text length: ${analysisText?.length || 0}`);
    console.log('First 500 chars of analysis:', analysisText?.substring(0, 500));
    const annotations = [];
    
    if (!analysisText || typeof analysisText !== 'string') {
      console.warn('Invalid analysis text provided to parseAnnotations');
      return annotations;
    }
    
    // Match patterns like [Page 5, Line 10] or [Page 5, Para 3]
    const regex = /\[Page\s+(\d+),\s+(?:Line|Para|Paragraph)\s+(\d+)\]/gi;
    let match;
    let iterations = 0;
    const MAX_ITERATIONS = 1000; // Safety limit
    
    while ((match = regex.exec(analysisText)) !== null && iterations < MAX_ITERATIONS) {
      iterations++;
      const pageNum = parseInt(match[1]);
      const lineNum = parseInt(match[2]);
      
      // Extract the sentence/paragraph containing the reference
      // Look backwards to find the start of the sentence (after . or newline)
      let sentenceStart = match.index;
      for (let i = match.index - 1; i >= 0; i--) {
        if (analysisText[i] === '.' || analysisText[i] === '\n') {
          sentenceStart = i + 1;
          break;
        }
        if (match.index - i > 200) break; // Don't go too far back
      }
      
      // Look forwards to find the end of the sentence (before . or newline)
      let sentenceEnd = match.index + match[0].length;
      for (let i = sentenceEnd; i < analysisText.length; i++) {
        if (analysisText[i] === '.' || analysisText[i] === '\n') {
          sentenceEnd = i + 1;
          break;
        }
        if (i - sentenceEnd > 200) break; // Don't go too far forward
      }
      
      // Extract the full sentence
      let fullSentence = analysisText.substring(sentenceStart, sentenceEnd).trim();
      
      // Remove the page reference marker from the text
      const textWithoutMarker = fullSentence.replace(/\[Page\s+\d+,\s+(?:Line|Para|Paragraph)\s+\d+\]/gi, '').trim();
      
      // Extract title from the sentence (first few words)
      const words = textWithoutMarker.split(/\s+/).slice(0, 8).join(' ');
      const title = words.length > 50 ? words.substring(0, 50) + '...' : words;
      
      annotations.push({
        documentId,
        page: pageNum,
        line: lineNum,
        title: title || `Finding ${annotations.length + 1}`,
        text: textWithoutMarker.substring(0, 250) + (textWithoutMarker.length > 250 ? '...' : ''),
        x: 85, // Position on right side
        y: 10 + (annotations.filter(a => a.page === pageNum).length * 12) // Stack vertically
      });
    }
    
    if (iterations >= MAX_ITERATIONS) {
      console.warn('parseAnnotations hit maximum iteration limit');
    }
    
    console.log(`Found ${annotations.length} annotations after ${iterations} iterations`);
    return annotations;
  };

  const handleAnalyze = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    if (analysisType === 'custom' && !customPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setAnalyzing(true);
    setError('');

    try {
      const analyzedDocs = [];
      const allAnnotations = {};

      // If multiple documents selected, analyze each and combine results
      if (selectedFiles.length === 1) {
        const response = await apiService.analyzeDocument(
          selectedFiles[0].id, 
          analysisType,
          analysisType === 'custom' ? customPrompt : null
        );
        const analysis = response.data.analysis;
        
        setAnalysisResult(analysis.result);
        
        // Store document for PDF viewer
        if (analysis.document && analysis.document.file_type === 'pdf') {
          analyzedDocs.push(analysis.document);
        }
      } else {
        // Multiple documents - create a combined analysis prompt
        let combinedAnalysis = `# Multi-Document Analysis\n\n`;
        combinedAnalysis += `Analyzing ${selectedFiles.length} documents:\n`;
        selectedFiles.forEach((doc, idx) => {
          combinedAnalysis += `${idx + 1}. ${doc.filename}\n`;
        });
        combinedAnalysis += `\n---\n\n`;
        
        // Analyze each document
        for (const doc of selectedFiles) {
          try {
            console.log(`Analyzing document ${doc.filename}...`);
            const response = await apiService.analyzeDocument(
              doc.id, 
              analysisType,
              analysisType === 'custom' ? customPrompt : null
            );
            console.log(`Received response for ${doc.filename}`);
            const analysis = response.data.analysis;
            
            combinedAnalysis += `## ${doc.filename}\n\n`;
            combinedAnalysis += analysis.result + `\n\n---\n\n`;
            
            // Store document for PDF viewer
            console.log(`Processing document data for ${doc.filename}`, analysis.document);
            if (analysis.document && analysis.document.file_type === 'pdf') {
              analyzedDocs.push(analysis.document);
            }
          } catch (err) {
            console.error(`Error analyzing ${doc.filename}:`, err);
            combinedAnalysis += `## ${doc.filename}\n\n**Error:** Failed to analyze this document.\n\n---\n\n`;
          }
        }
        
        console.log('Finished analyzing all documents');

        // Add summary section
        combinedAnalysis += `## Overall Summary\n\n`;
        combinedAnalysis += `Analyzed ${selectedFiles.length} documents in this batch. `;
        combinedAnalysis += `Review the individual analyses above for detailed insights.\n`;
        
        setAnalysisResult(combinedAnalysis);
      }
      
      // Store analyzed documents
      console.log('Analyzed documents:', analyzedDocs);
      setAnalyzedDocuments(analyzedDocs);
      
      // TEMPORARILY DISABLED: Refresh history after analysis completes
      // This was causing the UI to hang - will fix separately
      // try {
      //   await fetchAllAnalysisHistory();
      // } catch (err) {
      //   console.error('Error refreshing history:', err);
      // }
      
      console.log('Analysis complete! Check above for documents and annotations.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to analyze document(s). Please try again.');
      console.error('Analysis error:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleToggleDocument = (doc) => {
    const isSelected = selectedFiles.some(f => f.id === doc.id);
    if (isSelected) {
      setSelectedFiles(selectedFiles.filter(f => f.id !== doc.id));
    } else {
      setSelectedFiles([...selectedFiles, doc]);
    }
  };

  const handleSelectAllDocuments = () => {
    if (selectedFiles.length === caseDocuments.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles([...caseDocuments]);
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
    setSelectedFiles(selectedFiles.filter(f => f.id !== fileId));
  };

  // NEW: Unified Analysis Handler with STREAMING
  const handleUnifiedAnalysis = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    // Map tab index to analysis type
    const analysisTypeMap = {
      0: 'standard',
      1: 'constitution',
      2: 'bare_acts',
      3: 'criminal_law',
      4: 'civil_law',
      5: 'family_law',
      6: 'custom'
    };

    const currentAnalysisType = analysisTypeMap[tabValue] || 'standard';

    // Get custom prompt based on tab
    let customPromptValue = null;
    if (currentAnalysisType === 'standard' && analysisType === 'custom') {
      customPromptValue = customPrompt;
    } else if (currentAnalysisType === 'constitution' && constitutionalAnalysisType === 'custom') {
      customPromptValue = constitutionalCustomPrompt;
    } else if (currentAnalysisType === 'bare_acts' && bareActsAnalysisType === 'custom') {
      customPromptValue = bareActsCustomPrompt;
    } else if (currentAnalysisType === 'criminal_law' && criminalLawAnalysisType === 'custom') {
      customPromptValue = criminalLawCustomPrompt;
    } else if (currentAnalysisType === 'civil_law' && civilLawAnalysisType === 'custom') {
      customPromptValue = civilLawCustomPrompt;
    } else if (currentAnalysisType === 'family_law' && familyLawAnalysisType === 'custom') {
      customPromptValue = familyLawCustomPrompt;
    }

    // Validate custom prompt if needed
    if (customPromptValue !== null && !customPromptValue.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    // Get reference documents based on analysis type
    let referenceDocuments = [];
    if (currentAnalysisType === 'bare_acts') {
      if (selectedBareActs.length === 0) {
        setError('Please select at least one Bare Act to analyze with');
        return;
      }
      referenceDocuments = selectedBareActs.map(act => act.name);
    } else if (currentAnalysisType === 'criminal_law') {
      if (selectedCriminalLaws.length === 0) {
        setError('Please select at least one Criminal Law document to analyze with');
        return;
      }
      referenceDocuments = selectedCriminalLaws.map(law => law.name);
    } else if (currentAnalysisType === 'civil_law') {
      if (selectedCivilLaws.length === 0) {
        setError('Please select at least one Civil Law document to analyze with');
        return;
      }
      referenceDocuments = selectedCivilLaws.map(law => law.name);
    } else if (currentAnalysisType === 'family_law') {
      if (selectedFamilyLaws.length === 0) {
        setError('Please select at least one Family Law document to analyze with');
        return;
      }
      referenceDocuments = selectedFamilyLaws.map(law => law.name);
    }

    // Set appropriate loading state
    const setAnalyzingState = (value) => {
      switch (currentAnalysisType) {
        case 'standard':
          setAnalyzing(value);
          break;
        case 'constitution':
          setConstitutionalAnalyzing(value);
          break;
        case 'bare_acts':
          setBareActsAnalyzing(value);
          break;
        case 'criminal_law':
          setCriminalLawAnalyzing(value);
          break;
        case 'civil_law':
          setCivilLawAnalyzing(value);
          break;
        case 'family_law':
          setFamilyLawAnalyzing(value);
          break;
        default:
          setAnalyzing(value);
      }
    };

    setAnalyzingState(true);
    setError('');

    try {
      const analyzedDocs = [];
      let accumulatedAnalysis = '';
      let chunkCount = 0;

      // Use streaming API to get real-time results
      await apiService.analyzeUnifiedStream(
        selectedFiles[0].id,
        null,
        currentAnalysisType,
        customPromptValue,
        referenceDocuments,
        (chunk) => {
          console.log('Received chunk:', chunk.type);

          if (chunk.type === 'error') {
            setError(chunk.data);
            setAnalyzingState(false);
            return;
          }

          if (chunk.type === 'metadata') {
            console.log('Analysis metadata:', chunk.data);
            if (chunk.data.needs_chunking) {
              console.log('⚠️ Large documents detected - will stream in chunks');
            }
          }

          if (chunk.type === 'chunk') {
            const chunkData = chunk.data;
            chunkCount++;
            
            // Build analysis section
            const chunkAnalysis = `\n\n## ${chunkData.doc_name} - ${chunkData.pages}\n\n${chunkData.analysis}\n\n---\n`;
            accumulatedAnalysis += chunkAnalysis;

            // Update UI with accumulated analysis in real-time
            switch (currentAnalysisType) {
              case 'standard':
                setAnalysisResult(accumulatedAnalysis);
                break;
              case 'constitution':
                setConstitutionalAnalysis({
                  analysis: accumulatedAnalysis,
                  sources: [],
                  reference_documents: []
                });
                break;
              case 'bare_acts':
                setBareActsAnalysis({
                  analysis: accumulatedAnalysis,
                  sources: [],
                  acts_used: []
                });
                break;
              case 'criminal_law':
                setCriminalLawAnalysis({
                  analysis: accumulatedAnalysis,
                  sources: [],
                  laws_used: []
                });
                break;
              case 'civil_law':
                setCivilLawAnalysis({
                  analysis: accumulatedAnalysis,
                  sources: [],
                  laws_used: []
                });
                break;
              case 'family_law':
                setFamilyLawAnalysis({
                  analysis: accumulatedAnalysis,
                  sources: [],
                  laws_used: []
                });
                break;
              default:
                setAnalysisResult(accumulatedAnalysis);
            }

            console.log(`✓ Chunk ${chunkCount} received and displayed`);
          }

          if (chunk.type === 'analysis') {
            // Non-chunked analysis (small documents)
            const analysisResult = chunk.data;
            
            switch (currentAnalysisType) {
              case 'standard':
                setAnalysisResult(analysisResult);
                break;
              case 'constitution':
                setConstitutionalAnalysis({
                  analysis: analysisResult,
                  sources: [],
                  reference_documents: []
                });
                break;
              case 'bare_acts':
                setBareActsAnalysis({
                  analysis: analysisResult,
                  sources: [],
                  acts_used: []
                });
                break;
              case 'criminal_law':
                setCriminalLawAnalysis({
                  analysis: analysisResult,
                  sources: [],
                  laws_used: []
                });
                break;
              case 'civil_law':
                setCivilLawAnalysis({
                  analysis: analysisResult,
                  sources: [],
                  laws_used: []
                });
                break;
              case 'family_law':
                setFamilyLawAnalysis({
                  analysis: analysisResult,
                  sources: [],
                  laws_used: []
                });
                break;
              default:
                setAnalysisResult(analysisResult);
            }
          }

          if (chunk.type === 'complete') {
            console.log(`✓ ${currentAnalysisType} analysis complete!`, chunk.data);
            if (chunk.data.chunked) {
              console.log(`Processed ${chunk.data.total_chunks} chunks`);
            }
            
            // Save analysis to backend after completion
            // Use accumulated analysis or wait a bit for state to update
            setTimeout(async () => {
              await saveAnalysisToBackend(currentAnalysisType, accumulatedAnalysis, referenceDocuments);
            }, 500);
          }
        }
      );

      console.log(`✓ ${currentAnalysisType} streaming analysis complete`);

    } catch (err) {
      setError(err.message || 'Failed to perform analysis. Please try again.');
      console.error('Unified streaming analysis error:', err);
    } finally {
      setAnalyzingState(false);
    }
  };

  // Save analysis to backend for history
  const saveAnalysisToBackend = async (analysisType, analysisText, referenceDocuments = []) => {
    try {
      if (!selectedCase || !selectedFiles.length || !analysisText) {
        console.log('Skipping save - missing required data');
        return;
      }

      const analysisData = {
        case_id: selectedCase,
        document_id: selectedFiles[0].id,
        analysis_type: analysisType,
        analysis_text: analysisText,
        reference_documents: referenceDocuments,
        timestamp: new Date().toISOString()
      };

      await apiService.saveAnalysis(analysisData);
      console.log('✓ Analysis saved to history');
      
      // Refresh analysis history
      await fetchAllAnalysisHistory();
    } catch (err) {
      console.error('Error saving analysis:', err);
      // Don't show error to user - this is a background operation
    }
  };

  // Export analysis to PDF
  const exportAnalysisToPDF = async (analysisType, analysisContent) => {
    try {
      if (!analysisContent) {
        setError('No analysis to export');
        return;
      }

      // Prepare document with metadata
      const documentContent = `${analysisType.toUpperCase().replace('_', ' ')} ANALYSIS REPORT

Generated: ${new Date().toLocaleString()}
Case: ${selectedCase || 'N/A'}
Document: ${selectedFiles[0]?.name || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${analysisContent}`;

      // Call backend API to generate PDF
      const response = await apiService.exportDocumentPDF(documentContent);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${analysisType}_analysis_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✓ PDF exported successfully');
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF. Please try again.');
    }
  };

  const handleConstitutionalAnalysis = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    if (constitutionalAnalysisType === 'custom' && !constitutionalCustomPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setConstitutionalAnalyzing(true);
    setError('');

    try {
      const analyzedDocs = [];
      const allAnnotations = {};

      // Analyze first selected document with constitutional provisions
      const response = await apiService.constitutionalAnalysis(
        selectedFiles[0].id, 
        null,
        constitutionalAnalysisType,
        constitutionalAnalysisType === 'custom' ? constitutionalCustomPrompt : null
      );
      
      const analysisResult = response.data.constitutional_analysis || response.data.case_analysis || '';
      
      console.log('Constitutional analysis response:', response.data);
      console.log('Document in response:', response.data.document);
      
      setConstitutionalAnalysis({
        case_analysis: response.data.case_analysis,
        constitutional_analysis: response.data.constitutional_analysis,
        relevant_provisions: response.data.relevant_provisions || []
      });

      // Store document for PDF viewer if available
      if (response.data.document) {
        console.log('Document found! ID:', response.data.document.id, 'Type:', response.data.document.file_type);
        if (response.data.document.file_type === 'pdf') {
          analyzedDocs.push(response.data.document);
        }
      } else {
        console.log('No document in response!');
      }

      // Store analyzed documents
      console.log('Constitutional analysis - Analyzed documents:', analyzedDocs);
      setAnalyzedDocuments(analyzedDocs);
      
      // Refresh history after constitutional analysis completes
      try {
        await fetchAllAnalysisHistory();
      } catch (err) {
        console.error('Error refreshing history:', err);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to perform constitutional analysis. Please try again.');
      console.error('Constitutional analysis error:', err);
    } finally {
      setConstitutionalAnalyzing(false);
    }
  };

  const handleBareActsAnalysis = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    if (selectedBareActs.length === 0) {
      setError('Please select at least one Bare Act to analyze with');
      return;
    }

    if (bareActsAnalysisType === 'custom' && !bareActsCustomPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setBareActsAnalyzing(true);
    setError('');
    setTrainingStatus(null);

    try {
      // Check if any acts need training
      const actsToTrain = selectedBareActs.filter(act => act.status !== 'completed');
      
      if (actsToTrain.length > 0) {
        setTrainingInProgress(true);
        setTrainingStatus(`Training ${actsToTrain.length} Bare Act(s)...this may take a few moments`);
        
        // Note: Training happens on backend automatically during analysis
        // We just show the status here
      }
      
      const analyzedDocs = [];
      const allAnnotations = {};

      // Analyze with specific acts (backend will train if needed)
      const actNames = selectedBareActs.map(act => act.name);
      const response = await apiService.analyzeWithSpecificActs(
        selectedFiles[0].id, 
        null,
        actNames,
        bareActsAnalysisType,
        bareActsAnalysisType === 'custom' ? bareActsCustomPrompt : null
      );
      
      const analysisResult = response.data.analysis || '';
      
      setBareActsAnalysis({
        analysis: analysisResult,
        sources: response.data.sources || [],
        acts_used: response.data.acts_used || [],
        trained_acts: response.data.trained_acts || []
      });

      // Store document for PDF viewer if available
      if (response.data.document && response.data.document.file_type === 'pdf') {
        analyzedDocs.push(response.data.document);
      }

      // Store analyzed documents
      console.log('Bare Acts analysis - Analyzed documents:', analyzedDocs);
      setAnalyzedDocuments(analyzedDocs);
      
      setTrainingStatus(null);
      setTrainingInProgress(false);
      
      // Refresh acts list to update training status
      await fetchAvailableBareActs();
      
      // Refresh history after Bare Acts analysis completes
      try {
        await fetchAllAnalysisHistory();
      } catch (err) {
        console.error('Error refreshing history:', err);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to perform Bare Acts analysis. Please try again.');
      console.error('Bare Acts analysis error:', err);
      setTrainingStatus(null);
      setTrainingInProgress(false);
    } finally {
      setBareActsAnalyzing(false);
    }
  };

  const handleCriminalLawAnalysis = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    if (selectedCriminalLaws.length === 0) {
      setError('Please select at least one Criminal Law document to analyze with');
      return;
    }

    if (criminalLawAnalysisType === 'custom' && !criminalLawCustomPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setCriminalLawAnalyzing(true);
    setError('');

    try {
      const analyzedDocs = [];
      const allAnnotations = {};

      // Analyze with specific criminal law documents
      const lawNames = selectedCriminalLaws.map(law => law.name);
      const response = await apiService.analyzeWithCriminalLaw(
        selectedFiles[0].id, 
        null,
        lawNames,
        criminalLawAnalysisType,
        criminalLawAnalysisType === 'custom' ? criminalLawCustomPrompt : null
      );
      
      const analysisResult = response.data.analysis || '';
      
      setCriminalLawAnalysis({
        analysis: analysisResult,
        sources: response.data.sources || [],
        laws_used: response.data.laws_used || []
      });

      // Store document for PDF viewer if available
      if (response.data.document && response.data.document.file_type === 'pdf') {
        analyzedDocs.push(response.data.document);
      }

      // Store analyzed documents
      console.log('Criminal Law analysis - Analyzed documents:', analyzedDocs);
      setAnalyzedDocuments(analyzedDocs);
      
      // Refresh history after Criminal Law analysis completes
      try {
        await fetchAllAnalysisHistory();
      } catch (err) {
        console.error('Error refreshing history:', err);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to perform Criminal Law analysis. Please try again.');
      console.error('Criminal Law analysis error:', err);
    } finally {
      setCriminalLawAnalyzing(false);
    }
  };

  const handleCivilLawAnalysis = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    if (selectedCivilLaws.length === 0) {
      setError('Please select at least one Civil Law document to analyze with');
      return;
    }

    if (civilLawAnalysisType === 'custom' && !civilLawCustomPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setCivilLawAnalyzing(true);
    setError('');

    try {
      const analyzedDocs = [];
      const allAnnotations = {};

      // Analyze with specific civil law documents
      const lawNames = selectedCivilLaws.map(law => law.name);
      const response = await apiService.analyzeWithCivilLaw(
        selectedFiles[0].id, 
        null,
        lawNames,
        civilLawAnalysisType,
        civilLawAnalysisType === 'custom' ? civilLawCustomPrompt : null
      );
      
      const analysisResult = response.data.analysis || '';
      
      setCivilLawAnalysis({
        analysis: analysisResult,
        sources: response.data.sources || [],
        laws_used: response.data.laws_used || []
      });

      // Store document for PDF viewer if available
      if (response.data.document && response.data.document.file_type === 'pdf') {
        analyzedDocs.push(response.data.document);
      }

      // Store analyzed documents
      console.log('Civil Law analysis - Analyzed documents:', analyzedDocs);
      setAnalyzedDocuments(analyzedDocs);
      
      // Refresh history after Civil Law analysis completes
      try {
        await fetchAllAnalysisHistory();
      } catch (err) {
        console.error('Error refreshing history:', err);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to perform Civil Law analysis. Please try again.');
      console.error('Civil Law analysis error:', err);
    } finally {
      setCivilLawAnalyzing(false);
    }
  };

  const handleFamilyLawAnalysis = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    if (selectedFamilyLaws.length === 0) {
      setError('Please select at least one Family Law document to analyze with');
      return;
    }

    if (familyLawAnalysisType === 'custom' && !familyLawCustomPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setFamilyLawAnalyzing(true);
    setError('');

    try {
      const analyzedDocs = [];
      const allAnnotations = {};

      // Analyze with specific family law documents
      const lawNames = selectedFamilyLaws.map(law => law.name);
      const response = await apiService.analyzeWithFamilyLaw(
        selectedFiles[0].id, 
        null,
        lawNames,
        familyLawAnalysisType,
        familyLawAnalysisType === 'custom' ? familyLawCustomPrompt : null
      );
      
      const analysisResult = response.data.analysis || '';
      
      setFamilyLawAnalysis({
        analysis: analysisResult,
        sources: response.data.sources || [],
        laws_used: response.data.laws_used || []
      });

      // Store document for PDF viewer if available
      if (response.data.document && response.data.document.file_type === 'pdf') {
        analyzedDocs.push(response.data.document);
      }

      // Store analyzed documents
      console.log('Family Law analysis - Analyzed documents:', analyzedDocs);
      setAnalyzedDocuments(analyzedDocs);
      
      // Refresh history after Family Law analysis completes
      try {
        await fetchAllAnalysisHistory();
      } catch (err) {
        console.error('Error refreshing history:', err);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to perform Family Law analysis. Please try again.');
      console.error('Family Law analysis error:', err);
    } finally {
      setFamilyLawAnalyzing(false);
    }
  };

  const handleCustomAnalysis = async () => {
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one document to analyze');
      return;
    }

    if (!selectedCategory) {
      setError('Please select a custom model category');
      return;
    }

    if (customAnalysisType === 'custom' && !customCustomPrompt.trim()) {
      setError('Please enter a custom prompt');
      return;
    }

    setCustomAnalyzing(true);
    setError('');

    try {
      const analyzedDocs = [];
      const allAnnotations = {};

      // Analyze first selected document with custom model
      const response = await apiService.analyzeWithCustomModel(
        selectedCategory,
        selectedFiles[0].id, 
        null,
        customAnalysisType,
        customAnalysisType === 'custom' ? customCustomPrompt : null
      );
      
      const analysisResult = response.data.custom_analysis || response.data.case_summary || '';
      
      setCustomAnalysis({
        case_analysis: response.data.case_summary,
        custom_analysis: response.data.custom_analysis,
        relevant_sources: response.data.relevant_sources || []
      });

      // Store document for PDF viewer if available
      if (response.data.document && response.data.document.file_type === 'pdf') {
        analyzedDocs.push(response.data.document);
      }

      // Store analyzed documents
      console.log('Custom Model analysis - Analyzed documents:', analyzedDocs);
      setAnalyzedDocuments(analyzedDocs);
      
      // Refresh history after custom analysis completes
      try {
        await fetchAllAnalysisHistory();
      } catch (err) {
        console.error('Error refreshing history:', err);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to perform custom analysis. Please try again.');
      console.error('Custom analysis error:', err);
    } finally {
      setCustomAnalyzing(false);
    }
  };

  const fetchChatSessions = async () => {
    if (!selectedCase) return;
    
    try {
      setLoadingChat(true);
      const response = await apiService.getChatSessions(selectedCase);
      const sessions = response.data.sessions || [];
      setChatSessions(sessions);
      
      // Load the most recent session if exists
      if (sessions.length > 0 && !currentChatSession) {
        loadChatSession(sessions[0].id);
      }
    } catch (err) {
      console.error('Error fetching chat sessions:', err);
    } finally {
      setLoadingChat(false);
    }
  };

  const loadChatSession = async (sessionId) => {
    try {
      setLoadingChat(true);
      const response = await apiService.getChatSession(sessionId);
      const session = response.data.session;
      setCurrentChatSession(session);
      setChatMessages(session.messages || []);
    } catch (err) {
      console.error('Error loading chat session:', err);
      setError('Failed to load chat session');
    } finally {
      setLoadingChat(false);
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || sendingMessage) return;
    if (!selectedCase) {
      setError('Please select a case first');
      return;
    }
    if (selectedFiles.length === 0) {
      setError('Please select at least one document');
      return;
    }

    const message = chatInput.trim();
    setChatInput('');
    setSendingMessage(true);
    setError('');

    // Add user message to UI immediately
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setChatMessages([...chatMessages, userMessage]);

    try {
      // Get previous analysis context based on current tab
      let previousAnalysis = '';
      let analysisTypeContext = 'standard';
      
      if (tabValue === 0 && analysisResult) {
        previousAnalysis = analysisResult;
        analysisTypeContext = 'standard';
      } else if (tabValue === 1 && constitutionalAnalysis) {
        previousAnalysis = `Case Analysis:\n${constitutionalAnalysis.case_analysis}\n\nConstitutional Analysis:\n${constitutionalAnalysis.constitutional_analysis}`;
        analysisTypeContext = 'constitutional';
      } else if (tabValue === 2 && bareActsAnalysis) {
        previousAnalysis = bareActsAnalysis.analysis;
        analysisTypeContext = 'bare_acts';
      } else if (tabValue === 3 && criminalLawAnalysis) {
        previousAnalysis = criminalLawAnalysis.analysis;
        analysisTypeContext = 'criminal_law';
      } else if (tabValue === 4 && civilLawAnalysis) {
        previousAnalysis = civilLawAnalysis.analysis;
        analysisTypeContext = 'civil_law';
      } else if (tabValue === 5 && familyLawAnalysis) {
        previousAnalysis = familyLawAnalysis.analysis;
        analysisTypeContext = 'family_law';
      } else if (tabValue === 6 && customAnalysis) {
        previousAnalysis = `Case Analysis:\n${customAnalysis.case_analysis}\n\nCustom Analysis:\n${customAnalysis.custom_analysis}`;
        analysisTypeContext = 'custom';
      }

      const response = await apiService.sendChatMessage(
        selectedCase,
        message,
        selectedFiles.map(f => f.id),
        analysisTypeContext,
        currentChatSession?.id || null,
        previousAnalysis
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: response.data.timestamp
      };

      setChatMessages([...chatMessages, userMessage, aiMessage]);
      
      // Update current session ID if it's a new session
      if (!currentChatSession && response.data.chat_session_id) {
        setCurrentChatSession({ id: response.data.chat_session_id });
        // Refresh sessions list
        fetchChatSessions();
      }

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
      console.error('Chat error:', err);
      // Remove the optimistic user message on error
      setChatMessages(chatMessages);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartNewChat = () => {
    setCurrentChatSession(null);
    setChatMessages([]);
    setChatInput('');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear results when switching tabs
    if (newValue === 0) {
      // Standard Analysis
      setConstitutionalAnalysis(null);
      setBareActsAnalysis(null);
      setCustomAnalysis(null);
      setSelectedHistoryItem(null);
    } else if (newValue === 1) {
      // Constitutional Analysis
      setAnalysisResult(null);
      setBareActsAnalysis(null);
      setCustomAnalysis(null);
      setSelectedHistoryItem(null);
    } else if (newValue === 2) {
      // Bare Acts Analysis
      setAnalysisResult(null);
      setConstitutionalAnalysis(null);
      setCustomAnalysis(null);
      setSelectedHistoryItem(null);
    } else if (newValue === 3) {
      // Custom Analysis
      setAnalysisResult(null);
      setConstitutionalAnalysis(null);
      setBareActsAnalysis(null);
      setSelectedHistoryItem(null);
    } else if (newValue === 4) {
      // History tab - clear all results
      setAnalysisResult(null);
      setConstitutionalAnalysis(null);
      setBareActsAnalysis(null);
      setCustomAnalysis(null);
      setSelectedHistoryItem(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Case Selection on the right */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
            Document Analyzer
          </Typography>
          <Typography variant="body1" color="text.secondary">
            AI-powered analysis of legal documents - Select multiple documents for comprehensive analysis
          </Typography>
        </Box>
        
        {/* Case Selection - Top Right */}
        <Box sx={{ minWidth: 350, ml: 3 }}>
          <TextField
            fullWidth
            select
            label="Select Case"
            value={selectedCase}
            onChange={(e) => setSelectedCase(e.target.value)}
            required
            error={!selectedCase}
            size="medium"
            helperText={!selectedCase ? "Required" : `${caseDocuments.length} documents`}
          >
            {cases.length === 0 ? (
              <MenuItem disabled>No cases available</MenuItem>
            ) : (
              cases.map((caseItem) => (
                <MenuItem key={caseItem.id} value={caseItem.id}>
                  {caseItem.title}
                </MenuItem>
              ))
            )}
          </TextField>
          {cases.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Create a case first
            </Alert>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Analysis Type Tabs */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="Standard Analysis" 
            icon={<AnalyticsIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Constitutional Analysis" 
            icon={<GavelIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Bare Act Analysis" 
            icon={<SpeedIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Criminal Law Analysis" 
            icon={<GavelIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Civil Law Analysis" 
            icon={<GavelIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Family Law Analysis" 
            icon={<GavelIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Custom Analysis" 
            icon={<ModelTrainingIcon />} 
            iconPosition="start"
            disabled={customCategories.length === 0}
          />
          <Tab 
            label="Analysis History" 
            icon={<DescriptionIcon />} 
            iconPosition="start"
            disabled={!selectedCase}
          />
        </Tabs>
      </Card>

      {/* Document Selector - Shown for all tabs when case is selected */}
      {selectedCase && (
        <Card sx={{ mb: 2, boxShadow: 2 }}>
          <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Autocomplete
                  multiple
                  id="document-selector"
                  options={caseDocuments}
                  disableCloseOnSelect
                  value={selectedFiles}
                  onChange={(event, newValue) => {
                    setSelectedFiles(newValue);
                  }}
                  getOptionLabel={(option) => option.filename}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      <DescriptionIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                      {option.filename}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Documents to Analyze"
                      placeholder={selectedFiles.length === 0 ? "Choose documents..." : ""}
                      helperText={`${selectedFiles.length} of ${caseDocuments.length} documents selected`}
                    />
                  )}
                  sx={{ 
                    '& .MuiAutocomplete-inputRoot': { 
                      maxHeight: '50px',
                      overflow: 'auto'
                    } 
                  }}
                />
              </Box>
              <Box>
                <input {...getInputProps()} />
                <Button
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  onClick={() => {
                    if (!uploading) {
                      document.querySelector('input[type="file"]').click();
                    }
                  }}
                  disabled={!selectedCase || uploading}
                  sx={{ 
                    height: '56px',
                    minWidth: '140px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {uploading ? 'Uploading...' : 'Upload File'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Analysis Configuration & Results - Full Width */}
          {tabValue === 0 && (
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AnalyticsIcon color="primary" />
                  AI Analysis Configuration
                </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Analysis Type"
                    value={analysisType}
                    onChange={(e) => setAnalysisType(e.target.value)}
                  >
                    {quickAnalysisOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {analysisType === 'custom' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Custom Analysis Prompt"
                      value={customPrompt}
                      onChange={(e) => setCustomPrompt(e.target.value)}
                      placeholder="e.g., Identify all force majeure clauses and compare them to industry standards..."
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleUnifiedAnalysis}
                    disabled={!selectedCase || selectedFiles.length === 0 || analyzing}
                    startIcon={<AnalyticsIcon />}
                  >
                    {analyzing ? 'Analyzing...' : `Analyze ${selectedFiles.length > 0 ? `${selectedFiles.length} Document${selectedFiles.length > 1 ? 's' : ''}` : 'Documents'}`}
                  </Button>
                </Grid>
              </Grid>

              {analyzing && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    AI is analyzing {selectedFiles.length} document{selectedFiles.length > 1 ? 's' : ''}...
                    {selectedFiles.length > 1 && ' This may take a moment.'}
                  </Typography>
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1 }}>
                    ⚡ Streaming results in real-time as they complete
                  </Typography>
                  <LinearProgress />
                </Box>
              )}

              {!selectedCase && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please select a case first to continue
                </Alert>
              )}

              {selectedCase && selectedFiles.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Select one or more documents from the list to analyze
                </Alert>
              )}
            </CardContent>
          </Card>
          )}

          {/* Chat Window - Standard Analysis */}
          {tabValue === 0 && (analysisResult || chatMessages.length > 0) && (
            <Card sx={{ boxShadow: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0 }}>
                {/* Chat Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Analysis Chat
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={handleStartNewChat}
                      disabled={chatMessages.length === 0}
                    >
                      New Chat
                    </Button>
                    <Button
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={() => setSaveDialogOpen(true)}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => {/* Download logic */}}
                    >
                      Export
                    </Button>
                  </Box>
                </Box>

                {/* Chat Messages */}
                <Box sx={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  p: 2,
                  bgcolor: 'grey.50'
                }}>
                  {/* Initial Analysis as first message - ALWAYS SHOW */}
                  {analysisResult && (
                    <Paper sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip label="AI Assistant" size="small" color="primary" />
                        <Typography variant="caption" color="text.secondary">
                          Initial Analysis
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        '& h2': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 },
                        '& h3': { fontSize: '1.1rem', fontWeight: 600, mt: 1.5, mb: 1 },
                        '& ul, & ol': { pl: 3 },
                        '& li': { mb: 0.5 }
                      }}>
                        <ReactMarkdown
                          components={{
                            p: ({ node, ...props }) => {
                              // Extract text content from all children recursively
                              const extractText = (node) => {
                                if (!node) return '';
                                if (typeof node === 'string') return node;
                                if (node.type === 'text') return node.value || '';
                                if (node.children) {
                                  return node.children.map(extractText).join('');
                                }
                                return '';
                              };
                              
                              const text = node?.children ? node.children.map(extractText).join('') : '';
                              return <Typography paragraph>{renderAnalysisWithLinks(text)}</Typography>;
                            },
                            li: ({ node, ...props }) => {
                              const extractText = (node) => {
                                if (!node) return '';
                                if (typeof node === 'string') return node;
                                if (node.type === 'text') return node.value || '';
                                if (node.children) {
                                  return node.children.map(extractText).join('');
                                }
                                return '';
                              };
                              
                              const text = node?.children ? node.children.map(extractText).join('') : '';
                              return <li>{renderAnalysisWithLinks(text)}</li>;
                            }
                          }}
                        >
                          {analysisResult}
                        </ReactMarkdown>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => exportAnalysisToPDF('standard', analysisResult)}
                          size="small"
                        >
                          Export to PDF
                        </Button>
                      </Box>
                    </Paper>
                  )}

                  {/* PDF Viewer Modal - Hidden by default, shown when clicking page numbers */}

                  {/* Chat Messages */}
                  {chatMessages.map((msg, idx) => (
                    <Paper 
                      key={idx}
                      sx={{ 
                        p: 2, 
                        mb: 2,
                        bgcolor: msg.role === 'user' ? 'primary.light' : 'white',
                        ml: msg.role === 'user' ? 4 : 0,
                        mr: msg.role === 'user' ? 0 : 4
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip 
                          label={msg.role === 'user' ? 'You' : 'AI Assistant'} 
                          size="small" 
                          color={msg.role === 'user' ? 'default' : 'primary'}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                      <Box sx={{ 
                        '& h2': { fontSize: '1.1rem', fontWeight: 600, mt: 1, mb: 0.5 },
                        '& h3': { fontSize: '1rem', fontWeight: 600, mt: 1, mb: 0.5 },
                        '& ul, & ol': { pl: 3 },
                        '& li': { mb: 0.5 }
                      }}>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </Box>
                    </Paper>
                  ))}

                  {sendingMessage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        AI is thinking...
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Chat Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder="Ask follow-up questions about your documents..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={sendingMessage || !selectedCase || selectedFiles.length === 0}
                    InputProps={{
                      endAdornment: (
                        <IconButton 
                          onClick={handleSendMessage}
                          disabled={!chatInput.trim() || sendingMessage || !selectedCase || selectedFiles.length === 0}
                          color="primary"
                        >
                          <SendIcon />
                        </IconButton>
                      )
                    }}
                  />
                  {(!selectedCase || selectedFiles.length === 0) && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Select a case and documents to start chatting
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Constitutional Analysis Tab */}
          {tabValue === 1 && (
            <>
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GavelIcon color="primary" />
                    Constitutional Analysis Configuration
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Analyze documents against Indian Constitutional provisions, fundamental rights, and constitutional principles.
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Analysis Type"
                        value={constitutionalAnalysisType}
                        onChange={(e) => setConstitutionalAnalysisType(e.target.value)}
                      >
                        {constitutionalAnalysisOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {constitutionalAnalysisType === 'custom' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Custom Constitutional Analysis Prompt"
                          value={constitutionalCustomPrompt}
                          onChange={(e) => setConstitutionalCustomPrompt(e.target.value)}
                          placeholder="e.g., What constitutional rights are relevant to this case? Identify applicable fundamental rights..."
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleUnifiedAnalysis}
                        disabled={!selectedCase || selectedFiles.length === 0 || constitutionalAnalyzing}
                        startIcon={<GavelIcon />}
                      >
                        {constitutionalAnalyzing ? 'Analyzing...' : `Analyze with Constitution ${selectedFiles.length > 0 ? `(${selectedFiles.length} document${selectedFiles.length > 1 ? 's' : ''})` : ''}`}
                      </Button>
                    </Grid>
                  </Grid>

                  {constitutionalAnalyzing && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Performing constitutional analysis... This may take a moment.
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {!selectedCase && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please select a case first to continue
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more documents from the list to analyze
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Chat Window - Constitutional Analysis */}
              {(constitutionalAnalysis || chatMessages.length > 0) && (
                <Card sx={{ boxShadow: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GavelIcon color="primary" />
                        Constitutional Analysis Chat
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={handleStartNewChat} disabled={chatMessages.length === 0}>New Chat</Button>
                        <Button size="small" startIcon={<SaveIcon />} onClick={() => setSaveDialogOpen(true)}>Save</Button>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
                      {/* Initial Constitutional Analysis - ALWAYS SHOW */}
                      {constitutionalAnalysis && (
                        <Paper sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label="AI Assistant" size="small" color="primary" />
                            <Typography variant="caption" color="text.secondary">Constitutional Analysis</Typography>
                          </Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Constitutional Analysis</Typography>
                          <ReactMarkdown
                            components={{
                              p: ({ node, ...props }) => {
                                const extractText = (node) => {
                                  if (!node) return '';
                                  if (typeof node === 'string') return node;
                                  if (node.type === 'text') return node.value || '';
                                  if (node.children) {
                                    return node.children.map(extractText).join('');
                                  }
                                  return '';
                                };
                                
                                const text = node?.children ? node.children.map(extractText).join('') : '';
                                return <Typography paragraph>{renderAnalysisWithLinks(text)}</Typography>;
                              },
                              li: ({ node, ...props }) => {
                                const extractText = (node) => {
                                  if (!node) return '';
                                  if (typeof node === 'string') return node;
                                  if (node.type === 'text') return node.value || '';
                                  if (node.children) {
                                    return node.children.map(extractText).join('');
                                  }
                                  return '';
                                };
                                
                                const text = node?.children ? node.children.map(extractText).join('') : '';
                                return <li>{renderAnalysisWithLinks(text)}</li>;
                              }
                            }}
                          >
                            {constitutionalAnalysis.analysis || constitutionalAnalysis.constitutional_analysis || ''}
                          </ReactMarkdown>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                              variant="contained"
                              startIcon={<DownloadIcon />}
                              onClick={() => exportAnalysisToPDF('constitutional', constitutionalAnalysis.analysis || constitutionalAnalysis.constitutional_analysis || '')}
                              size="small"
                            >
                              Export to PDF
                            </Button>
                          </Box>
                        </Paper>
                      )}

                      {chatMessages.map((msg, idx) => (
                        <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: msg.role === 'user' ? 'primary.light' : 'white', ml: msg.role === 'user' ? 4 : 0, mr: msg.role === 'user' ? 0 : 4 }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label={msg.role === 'user' ? 'You' : 'AI Assistant'} size="small" color={msg.role === 'user' ? 'default' : 'primary'} />
                            <Typography variant="caption" color="text.secondary">{new Date(msg.timestamp).toLocaleTimeString()}</Typography>
                          </Box>
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      ))}

                      {sendingMessage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">AI is thinking...</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                      <TextField
                        fullWidth multiline maxRows={3}
                        placeholder="Ask about constitutional provisions..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={sendingMessage || !selectedCase || selectedFiles.length === 0}
                        InputProps={{
                          endAdornment: (
                            <IconButton onClick={handleSendMessage} disabled={!chatInput.trim() || sendingMessage} color="primary">
                              <SendIcon />
                            </IconButton>
                          )
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}

        {/* Bare Acts Analysis Tab */}
        {tabValue === 2 && (
            <>
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SpeedIcon color="primary" />
                    Bare Act Analysis Configuration
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Analyze documents against specific Indian Bare Acts (statutes). Select one or more acts to analyze with.
                    {availableBareActs.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {availableBareActs.filter(a => a.status === 'completed').length} of {availableBareActs.length} acts trained
                      </Typography>
                    )}
                  </Alert>

                  {/* Training Status Alert */}
                  {trainingStatus && (
                    <Alert severity="warning" sx={{ mb: 2 }} icon={<ModelTrainingIcon />}>
                      {trainingStatus}
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    {/* Bare Acts Selection */}
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={availableBareActs}
                        getOptionLabel={(option) => option.name}
                        value={selectedBareActs}
                        onChange={(event, newValue) => setSelectedBareActs(newValue)}
                        loading={bareActsLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Bare Acts"
                            placeholder="Search and select acts..."
                            helperText="Select one or more Bare Acts to analyze with"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {option.name}
                              </Typography>
                              {option.status === 'completed' ? (
                                <Chip label="Trained" size="small" color="success" />
                              ) : option.status === 'failed' ? (
                                <Chip label="Failed" size="small" color="error" />
                              ) : (
                                <Chip label="Not Trained" size="small" color="default" />
                              )}
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.name}
                              {...getTagProps({ index })}
                              size="small"
                              color={option.status === 'completed' ? 'success' : 'default'}
                            />
                          ))
                        }
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options;
                          return options.filter(option =>
                            option.name.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Analysis Type"
                        value={bareActsAnalysisType}
                        onChange={(e) => setBareActsAnalysisType(e.target.value)}
                      >
                        {constitutionalAnalysisOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {bareActsAnalysisType === 'custom' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Custom Bare Act Analysis Prompt"
                          value={bareActsCustomPrompt}
                          onChange={(e) => setBareActsCustomPrompt(e.target.value)}
                          placeholder="e.g., What provisions of IPC apply to this case? Identify applicable sections..."
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleUnifiedAnalysis}
                        disabled={!selectedCase || selectedFiles.length === 0 || selectedBareActs.length === 0 || bareActsAnalyzing}
                        startIcon={<SpeedIcon />}
                      >
                        {bareActsAnalyzing 
                          ? 'Analyzing...' 
                          : `Analyze with ${selectedBareActs.length > 0 ? `${selectedBareActs.length} Act(s)` : 'Bare Acts'}`}
                      </Button>
                    </Grid>
                  </Grid>

                  {bareActsAnalyzing && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Performing Bare Acts analysis... This may take a moment.
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {!selectedCase && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please select a case first to continue
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more documents from the list to analyze
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length > 0 && selectedBareActs.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more Bare Acts from the dropdown above
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Chat Window - Bare Acts Analysis */}
              {(bareActsAnalysis || chatMessages.length > 0) && (
                <Card sx={{ boxShadow: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SpeedIcon color="primary" />
                        Bare Acts Analysis Chat
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={handleStartNewChat} disabled={chatMessages.length === 0}>New Chat</Button>
                        <Button size="small" startIcon={<SaveIcon />} onClick={() => setSaveDialogOpen(true)}>Save</Button>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
                      {/* Initial Bare Acts Analysis - ALWAYS SHOW */}
                      {bareActsAnalysis && (
                        <Paper sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label="AI Assistant" size="small" color="primary" />
                            <Typography variant="caption" color="text.secondary">Bare Acts Analysis</Typography>
                          </Box>
                          {bareActsAnalysis.acts_used && bareActsAnalysis.acts_used.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Acts Analyzed:</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {bareActsAnalysis.acts_used.map((act, idx) => (
                                  <Chip key={idx} label={act} color="primary" size="small" />
                                ))}
                              </Box>
                            </Box>
                          )}
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Analysis:</Typography>
                          <ReactMarkdown>{bareActsAnalysis.analysis}</ReactMarkdown>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                              variant="contained"
                              startIcon={<DownloadIcon />}
                              onClick={() => exportAnalysisToPDF('bare_acts', bareActsAnalysis.analysis)}
                              size="small"
                            >
                              Export to PDF
                            </Button>
                          </Box>
                        </Paper>
                      )}

                      {chatMessages.map((msg, idx) => (
                        <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: msg.role === 'user' ? 'primary.light' : 'white', ml: msg.role === 'user' ? 4 : 0, mr: msg.role === 'user' ? 0 : 4 }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label={msg.role === 'user' ? 'You' : 'AI Assistant'} size="small" color={msg.role === 'user' ? 'default' : 'primary'} />
                            <Typography variant="caption" color="text.secondary">{new Date(msg.timestamp).toLocaleTimeString()}</Typography>
                          </Box>
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      ))}

                      {sendingMessage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">AI is thinking...</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                      <TextField
                        fullWidth multiline maxRows={3}
                        placeholder="Ask about specific acts and provisions..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={sendingMessage || !selectedCase || selectedFiles.length === 0}
                        InputProps={{
                          endAdornment: (
                            <IconButton onClick={handleSendMessage} disabled={!chatInput.trim() || sendingMessage} color="primary">
                              <SendIcon />
                            </IconButton>
                          )
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Criminal Law Analysis Tab */}
          {tabValue === 3 && (
            <>
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SpeedIcon color="primary" />
                    Criminal Law Analysis Configuration
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Analyze documents against Indian Criminal Laws (IPC, CrPC, Evidence Act, New Criminal Laws, etc.). Select one or more laws to analyze with.
                  </Alert>

                  <Grid container spacing={2}>
                    {/* Criminal Laws Selection */}
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={availableCriminalLaws}
                        getOptionLabel={(option) => option.name}
                        value={selectedCriminalLaws}
                        onChange={(event, newValue) => setSelectedCriminalLaws(newValue)}
                        loading={criminalLawLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Criminal Laws"
                            placeholder="Search and select laws..."
                            helperText="Select one or more Criminal Law documents to analyze with"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {option.name}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.name}
                              {...getTagProps({ index })}
                              size="small"
                              color="primary"
                            />
                          ))
                        }
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options;
                          return options.filter(option =>
                            option.name.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Analysis Type"
                        value={criminalLawAnalysisType}
                        onChange={(e) => setCriminalLawAnalysisType(e.target.value)}
                      >
                        {constitutionalAnalysisOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {criminalLawAnalysisType === 'custom' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Custom Criminal Law Analysis Prompt"
                          value={criminalLawCustomPrompt}
                          onChange={(e) => setCriminalLawCustomPrompt(e.target.value)}
                          placeholder="e.g., What provisions of IPC apply to this case? Identify applicable sections..."
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleUnifiedAnalysis}
                        disabled={!selectedCase || selectedFiles.length === 0 || criminalLawAnalyzing}
                        startIcon={criminalLawAnalyzing ? <CircularProgress size={20} /> : <SpeedIcon />}
                      >
                        {criminalLawAnalyzing 
                          ? 'Analyzing...' 
                          : 'Analyze with Criminal Laws'}
                      </Button>
                    </Grid>
                  </Grid>

                  {criminalLawAnalyzing && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Performing Criminal Law analysis... This may take a moment.
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {!selectedCase && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please select a case first to continue
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more documents from the list to analyze
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length > 0 && selectedCriminalLaws.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more Criminal Laws from the dropdown above
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Results Display */}
              {criminalLawAnalysis && (
                <Card sx={{ mb: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="primary" />
                      Criminal Law Analysis Results
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <ReactMarkdown>{criminalLawAnalysis.analysis}</ReactMarkdown>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportAnalysisToPDF('criminal_law', criminalLawAnalysis.analysis)}
                        size="small"
                      >
                        Export to PDF
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Chat Window */}
              {criminalLawAnalysis && (
                <Card sx={{ mb: 3, boxShadow: 2, maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.main', color: 'white' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon />
                        AI Legal Assistant - Criminal Law
                      </Typography>
                      <Typography variant="caption">
                        Ask follow-up questions about the criminal law analysis
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f5f5f5', minHeight: '200px', maxHeight: '300px' }}>
                      {chatMessages.map((msg, idx) => (
                        <Paper key={idx} elevation={1} sx={{ p: 2, mb: 2, bgcolor: msg.role === 'user' ? 'primary.light' : 'white', color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {msg.role === 'user' ? 'You' : 'AI Assistant'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(msg.timestamp).toLocaleTimeString()}</Typography>
                          </Box>
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      ))}

                      {sendingMessage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">AI is thinking...</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                      <TextField
                        fullWidth multiline maxRows={3}
                        placeholder="Ask about specific laws and provisions..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={sendingMessage || !selectedCase || selectedFiles.length === 0}
                        InputProps={{
                          endAdornment: (
                            <IconButton onClick={handleSendMessage} disabled={!chatInput.trim() || sendingMessage} color="primary">
                              <SendIcon />
                            </IconButton>
                          )
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Civil Law Analysis Tab */}
          {tabValue === 4 && (
            <>
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SpeedIcon color="primary" />
                    Civil Law Analysis Configuration
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Analyze documents against Indian Civil Laws (CPC, Contract Act, Companies Act, etc.). Select one or more laws to analyze with.
                  </Alert>

                  <Grid container spacing={2}>
                    {/* Civil Laws Selection */}
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={availableCivilLaws}
                        getOptionLabel={(option) => option.name}
                        value={selectedCivilLaws}
                        onChange={(event, newValue) => setSelectedCivilLaws(newValue)}
                        loading={civilLawLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Civil Laws"
                            placeholder="Search and select laws..."
                            helperText="Select one or more Civil Law documents to analyze with"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {option.name}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.name}
                              {...getTagProps({ index })}
                              size="small"
                              color="primary"
                            />
                          ))
                        }
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options;
                          return options.filter(option =>
                            option.name.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Analysis Type"
                        value={civilLawAnalysisType}
                        onChange={(e) => setCivilLawAnalysisType(e.target.value)}
                      >
                        {constitutionalAnalysisOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {civilLawAnalysisType === 'custom' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Custom Civil Law Analysis Prompt"
                          value={civilLawCustomPrompt}
                          onChange={(e) => setCivilLawCustomPrompt(e.target.value)}
                          placeholder="e.g., What contract law provisions apply? Analyze under CPC..."
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleUnifiedAnalysis}
                        disabled={!selectedCase || selectedFiles.length === 0 || civilLawAnalyzing}
                        startIcon={civilLawAnalyzing ? <CircularProgress size={20} /> : <SpeedIcon />}
                      >
                        {civilLawAnalyzing 
                          ? 'Analyzing...' 
                          : 'Analyze with Civil Laws'}
                      </Button>
                    </Grid>
                  </Grid>

                  {civilLawAnalyzing && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Performing Civil Law analysis... This may take a moment.
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {!selectedCase && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please select a case first to continue
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more documents from the list to analyze
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length > 0 && selectedCivilLaws.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more Civil Laws from the dropdown above
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Results Display */}
              {civilLawAnalysis && (
                <Card sx={{ mb: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="primary" />
                      Civil Law Analysis Results
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <ReactMarkdown>{civilLawAnalysis.analysis}</ReactMarkdown>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportAnalysisToPDF('civil_law', civilLawAnalysis.analysis)}
                        size="small"
                      >
                        Export to PDF
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Chat Window */}
              {civilLawAnalysis && (
                <Card sx={{ mb: 3, boxShadow: 2, maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.main', color: 'white' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon />
                        AI Legal Assistant - Civil Law
                      </Typography>
                      <Typography variant="caption">
                        Ask follow-up questions about the civil law analysis
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f5f5f5', minHeight: '200px', maxHeight: '300px' }}>
                      {chatMessages.map((msg, idx) => (
                        <Paper key={idx} elevation={1} sx={{ p: 2, mb: 2, bgcolor: msg.role === 'user' ? 'primary.light' : 'white', color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {msg.role === 'user' ? 'You' : 'AI Assistant'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(msg.timestamp).toLocaleTimeString()}</Typography>
                          </Box>
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      ))}

                      {sendingMessage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">AI is thinking...</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                      <TextField
                        fullWidth multiline maxRows={3}
                        placeholder="Ask about specific laws and provisions..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={sendingMessage || !selectedCase || selectedFiles.length === 0}
                        InputProps={{
                          endAdornment: (
                            <IconButton onClick={handleSendMessage} disabled={!chatInput.trim() || sendingMessage} color="primary">
                              <SendIcon />
                            </IconButton>
                          )
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Family Law Analysis Tab */}
          {tabValue === 5 && (
            <>
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SpeedIcon color="primary" />
                    Family Law Analysis Configuration
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Analyze documents against Indian Family Laws (Hindu and Muslim personal laws). Select one or more laws to analyze with.
                  </Alert>

                  <Grid container spacing={2}>
                    {/* Family Laws Selection */}
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        options={availableFamilyLaws}
                        getOptionLabel={(option) => option.name}
                        value={selectedFamilyLaws}
                        onChange={(event, newValue) => setSelectedFamilyLaws(newValue)}
                        loading={familyLawLoading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Family Laws"
                            placeholder="Search and select laws..."
                            helperText="Select one or more Family Law documents to analyze with"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {option.name}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option.name}
                              {...getTagProps({ index })}
                              size="small"
                              color="primary"
                            />
                          ))
                        }
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options;
                          return options.filter(option =>
                            option.name.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Analysis Type"
                        value={familyLawAnalysisType}
                        onChange={(e) => setFamilyLawAnalysisType(e.target.value)}
                      >
                        {constitutionalAnalysisOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {familyLawAnalysisType === 'custom' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Custom Family Law Analysis Prompt"
                          value={familyLawCustomPrompt}
                          onChange={(e) => setFamilyLawCustomPrompt(e.target.value)}
                          placeholder="e.g., What marriage law provisions apply? Analyze succession rights..."
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleUnifiedAnalysis}
                        disabled={!selectedCase || selectedFiles.length === 0 || familyLawAnalyzing}
                        startIcon={familyLawAnalyzing ? <CircularProgress size={20} /> : <SpeedIcon />}
                      >
                        {familyLawAnalyzing 
                          ? 'Analyzing...' 
                          : 'Analyze with Family Laws'}
                      </Button>
                    </Grid>
                  </Grid>

                  {familyLawAnalyzing && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Performing Family Law analysis... This may take a moment.
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {!selectedCase && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please select a case first to continue
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more documents from the list to analyze
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length > 0 && selectedFamilyLaws.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more Family Laws from the dropdown above
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Results Display */}
              {familyLawAnalysis && (
                <Card sx={{ mb: 3, boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon color="primary" />
                      Family Law Analysis Results
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <ReactMarkdown>{familyLawAnalysis.analysis}</ReactMarkdown>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => exportAnalysisToPDF('family_law', familyLawAnalysis.analysis)}
                        size="small"
                      >
                        Export to PDF
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Chat Window */}
              {familyLawAnalysis && (
                <Card sx={{ mb: 3, boxShadow: 2, maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'primary.main', color: 'white' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatIcon />
                        AI Legal Assistant - Family Law
                      </Typography>
                      <Typography variant="caption">
                        Ask follow-up questions about the family law analysis
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f5f5f5', minHeight: '200px', maxHeight: '300px' }}>
                      {chatMessages.map((msg, idx) => (
                        <Paper key={idx} elevation={1} sx={{ p: 2, mb: 2, bgcolor: msg.role === 'user' ? 'primary.light' : 'white', color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {msg.role === 'user' ? 'You' : 'AI Assistant'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(msg.timestamp).toLocaleTimeString()}</Typography>
                          </Box>
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      ))}

                      {sendingMessage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">AI is thinking...</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                      <TextField
                        fullWidth multiline maxRows={3}
                        placeholder="Ask about specific laws and provisions..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={sendingMessage || !selectedCase || selectedFiles.length === 0}
                        InputProps={{
                          endAdornment: (
                            <IconButton onClick={handleSendMessage} disabled={!chatInput.trim() || sendingMessage} color="primary">
                              <SendIcon />
                            </IconButton>
                          )
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Custom Analysis Tab */}
          {tabValue === 6 && (
            <>
              <Card sx={{ mb: 3, boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ModelTrainingIcon color="primary" />
                    Custom Model Analysis Configuration
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Analyze documents using your trained custom AI models. Train models in the Custom Models page.
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Select Custom Model"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        {customCategories.length === 0 ? (
                          <MenuItem disabled>No trained models available</MenuItem>
                        ) : (
                          customCategories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name} ({category.num_chunks || 0} chunks)
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Analysis Type"
                        value={customAnalysisType}
                        onChange={(e) => setCustomAnalysisType(e.target.value)}
                      >
                        {constitutionalAnalysisOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {customAnalysisType === 'custom' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Custom Analysis Prompt"
                          value={customCustomPrompt}
                          onChange={(e) => setCustomCustomPrompt(e.target.value)}
                          placeholder="e.g., Compare this case to the precedents in my training documents..."
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleCustomAnalysis}
                        disabled={!selectedCase || selectedFiles.length === 0 || !selectedCategory || customAnalyzing}
                        startIcon={<ModelTrainingIcon />}
                      >
                        {customAnalyzing ? 'Analyzing...' : `Analyze with Custom Model ${selectedFiles.length > 0 ? `(${selectedFiles.length} document${selectedFiles.length > 1 ? 's' : ''})` : ''}`}
                      </Button>
                    </Grid>
                  </Grid>

                  {customAnalyzing && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Performing custom model analysis... This may take a moment.
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {!selectedCase && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please select a case first to continue
                    </Alert>
                  )}

                  {selectedCase && selectedFiles.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Select one or more documents from the list to analyze
                    </Alert>
                  )}

                  {customCategories.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No trained models available. Go to Custom Models page to create and train models.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Chat Window - Custom Analysis */}
              {(customAnalysis || chatMessages.length > 0) && (
                <Card sx={{ boxShadow: 2, height: '600px', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 0 }}>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ModelTrainingIcon color="primary" />
                        Custom Model Chat
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={handleStartNewChat} disabled={chatMessages.length === 0}>New Chat</Button>
                        <Button size="small" startIcon={<SaveIcon />} onClick={() => setSaveDialogOpen(true)}>Save</Button>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
                      {/* Initial Custom Analysis - ALWAYS SHOW */}
                      {customAnalysis && (
                        <Paper sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label="AI Assistant" size="small" color="primary" />
                            <Typography variant="caption" color="text.secondary">Custom Model Analysis</Typography>
                          </Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Case Summary</Typography>
                          <ReactMarkdown>{customAnalysis.case_analysis}</ReactMarkdown>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Custom Analysis</Typography>
                          <ReactMarkdown>{customAnalysis.custom_analysis}</ReactMarkdown>
                        </Paper>
                      )}

                      {chatMessages.map((msg, idx) => (
                        <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: msg.role === 'user' ? 'primary.light' : 'white', ml: msg.role === 'user' ? 4 : 0, mr: msg.role === 'user' ? 0 : 4 }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip label={msg.role === 'user' ? 'You' : 'AI Assistant'} size="small" color={msg.role === 'user' ? 'default' : 'primary'} />
                            <Typography variant="caption" color="text.secondary">{new Date(msg.timestamp).toLocaleTimeString()}</Typography>
                          </Box>
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </Paper>
                      ))}

                      {sendingMessage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">AI is thinking...</Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                      <TextField
                        fullWidth multiline maxRows={3}
                        placeholder="Ask questions about your custom trained model..."
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                        disabled={sendingMessage || !selectedCase || selectedFiles.length === 0}
                        InputProps={{
                          endAdornment: (
                            <IconButton onClick={handleSendMessage} disabled={!chatInput.trim() || sendingMessage} color="primary">
                              <SendIcon />
                            </IconButton>
                          )
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* History Tab */}
          {tabValue === 7 && (
            <>
              <Card sx={{ boxShadow: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    Analysis History - All Analyses
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    View all analyses and prompts for this case
                  </Typography>

                  {loadingHistory ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Standard Analyses History */}
                      {analysisHistory.length > 0 && (
                        <>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                            Standard Analyses ({analysisHistory.length})
                          </Typography>
                          <List>
                            {analysisHistory.map((analysis) => {
                              const doc = caseDocuments.find(d => d.id === analysis.document_id);
                              const analysisTypeLabel = analysis.analysis_type?.replace('_', ' ').toUpperCase() || 'ANALYSIS';
                              
                              return (
                                <ListItem
                                  key={analysis.id}
                                  sx={{
                                    mb: 1,
                                    bgcolor: selectedHistoryItem?.id === analysis.id ? 'primary.50' : 'grey.50',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: selectedHistoryItem?.id === analysis.id ? 'primary.100' : 'grey.100' }
                                  }}
                                  onClick={() => setSelectedHistoryItem(analysis)}
                                >
                                  <ListItemIcon>
                                    <AnalyticsIcon color="primary" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <>
                                        {analysisTypeLabel}
                                        {analysis.query && (
                                          <Typography component="span" variant="body2" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                            - "{analysis.query.length > 50 ? analysis.query.substring(0, 50) + '...' : analysis.query}"
                                          </Typography>
                                        )}
                                      </>
                                    }
                                    secondary={
                                      <>
                                        {doc && <span>{doc.filename} • </span>}
                                        {analysis.created_at 
                                          ? new Date(analysis.created_at.seconds * 1000).toLocaleString()
                                          : 'Unknown date'}
                                      </>
                                    }
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                          <Divider sx={{ my: 3 }} />
                        </>
                      )}

                      {/* Constitutional Analyses History */}
                      {constitutionalHistory.length > 0 && (
                        <>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                            Constitutional Analyses ({constitutionalHistory.length})
                          </Typography>
                          <List>
                            {constitutionalHistory.map((analysis) => {
                              const doc = caseDocuments.find(d => d.id === analysis.document_id);
                              const analysisTypeLabel = analysis.constitutional_analysis_type?.replace('_', ' ').toUpperCase() || 'CONSTITUTIONAL';
                              
                              return (
                                <ListItem
                                  key={analysis.id}
                                  sx={{
                                    mb: 1,
                                    bgcolor: selectedHistoryItem?.id === analysis.id ? 'primary.50' : 'grey.50',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: selectedHistoryItem?.id === analysis.id ? 'primary.100' : 'grey.100' }
                                  }}
                                  onClick={() => setSelectedHistoryItem(analysis)}
                                >
                                  <ListItemIcon>
                                    <GavelIcon color="primary" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <>
                                        {analysisTypeLabel} Analysis
                                        {analysis.custom_prompt && (
                                          <Typography component="span" variant="body2" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                            - "{analysis.custom_prompt.length > 50 ? analysis.custom_prompt.substring(0, 50) + '...' : analysis.custom_prompt}"
                                          </Typography>
                                        )}
                                      </>
                                    }
                                    secondary={
                                      <>
                                        {doc && <span>{doc.filename} • </span>}
                                        {analysis.created_at 
                                          ? new Date(analysis.created_at.seconds * 1000).toLocaleString()
                                          : 'Unknown date'}
                                        {analysis.custom_prompt && <Chip label="Custom Prompt" size="small" sx={{ ml: 1, height: 16 }} />}
                                      </>
                                    }
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                          <Divider sx={{ my: 3 }} />
                        </>
                      )}

                      {/* Empty State */}
                      {analysisHistory.length === 0 && constitutionalHistory.length === 0 && !loadingHistory && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No analysis history for this case yet
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Run an analysis on any document to start building your history
                          </Typography>
                        </Box>
                      )}

                      {/* Selected History Item Display */}
                      {selectedHistoryItem && (
                        <Card sx={{ mt: 3, bgcolor: 'background.paper' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {selectedHistoryItem.analysis_type === 'constitutional' 
                                  ? 'Constitutional Analysis Details'
                                  : 'Analysis Details'}
                              </Typography>
                              <IconButton size="small" onClick={() => setSelectedHistoryItem(null)}>
                                <DeleteIcon />
                              </IconButton>
                            </Box>

                            {selectedHistoryItem.analysis_type === 'constitutional' ? (
                              <>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                  Case Summary
                                </Typography>
                                <Box sx={{ 
                                  p: 2, 
                                  bgcolor: 'grey.50', 
                                  borderRadius: 1, 
                                  mb: 2,
                                  maxHeight: '300px',
                                  overflow: 'auto'
                                }}>
                                  <ReactMarkdown>{selectedHistoryItem.case_summary}</ReactMarkdown>
                                </Box>

                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                  Constitutional Analysis
                                </Typography>
                                <Box sx={{ 
                                  p: 2, 
                                  bgcolor: 'grey.50', 
                                  borderRadius: 1,
                                  maxHeight: '400px',
                                  overflow: 'auto'
                                }}>
                                  <ReactMarkdown>{selectedHistoryItem.constitutional_analysis}</ReactMarkdown>
                                </Box>
                              </>
                            ) : (
                              <Box sx={{ 
                                p: 2, 
                                bgcolor: 'grey.50', 
                                borderRadius: 1,
                                maxHeight: '500px',
                                overflow: 'auto'
                              }}>
                                <ReactMarkdown>{selectedHistoryItem.result}</ReactMarkdown>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </>
          )}

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Analysis</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save this analysis to a case or the Research Vault
          </Typography>
          <TextField
            fullWidth
            select
            label="Save Location"
            margin="normal"
          >
            <MenuItem value="case">Associated Case</MenuItem>
            <MenuItem value="vault">Research Vault</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Analysis Title"
            margin="normal"
            placeholder="e.g., Contract Risk Analysis - ABC Corp"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setSaveDialogOpen(false)}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Viewer Modal */}
      <Dialog
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {pdfModalDocument?.filename || 'Document'}
          <IconButton
            aria-label="close"
            onClick={() => setPdfModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {pdfModalDocument && (
            <PDFViewer
              documentUrl={pdfModalDocument.download_url}
              documentName={pdfModalDocument.filename}
              annotations={[]}
              initialPage={pdfModalPage}
              onPageChange={(page) => {
                console.log(`PDF Modal - Page ${page}`);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Analyzer;